/**
 * @file
 * Card Gallery component behavior.
 *
 * Conditionally initializes Swiper carousel and enhances accessibility.
 *
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 * @param {object} Swiper - The Swiper library.
 */

(function (Drupal, once, Swiper) {
  'use strict';

  const SELECTORS = {
    gallery: '.card-gallery',
    swiper: '.card-gallery--multiple .swiper',
    slide: '.swiper-slide',
    link: '.journey-card__wrapper',
    prevButton: '.swiper-button-prev',
    nextButton: '.swiper-button-next',
    pagination: '.swiper-pagination',
    bullet: '.swiper-pagination-bullet',
    bulletActive: 'swiper-pagination-bullet-active',
    disabled: 'swiper-button-disabled'
  };

  /**
   * Inject carousel position into each journey card link aria-label.
   *
   * Reads the existing aria-label set in Twig and appends "N of Y" so screen
   * readers announce the full context in one phrase. Called once on afterInit.
   *
   * @param {object} swiper - Initialized Swiper instance.
   */
  function updateLinkAriaLabels(swiper) {
    const total = swiper.slides.length;
    swiper.slides.forEach(function (slide, index) {
      const link = slide.querySelector(SELECTORS.link);
      if (!link) {
        return;
      }
      const base = link.getAttribute('aria-label') || '';
      const position = Drupal.t('@index of @total', {
        '@index': index + 1,
        '@total': total
      });
      link.setAttribute('aria-label', base + ', ' + position);
    });
  }

  /**
   * Toggle carousel ARIA attributes based on whether Swiper is locked.
   *
   * When locked (all slides visible, no overflow), the component behaves as a
   * static list. Remove aria-roledescription so it is not announced as a
   * carousel. When unlocked, restore carousel semantics.
   *
   * @param {object} swiper - Swiper instance.
   * @param {boolean} isLocked - Whether the carousel is locked.
   */
  function updateCarouselAria(swiper, isLocked) {
    const el = swiper.el;

    if (isLocked) {
      el.removeAttribute('role');
      el.removeAttribute('aria-roledescription');
      el.removeAttribute('aria-label');
      el.removeAttribute('tabindex');
      return;
    }

    el.setAttribute('role', 'application');
    el.setAttribute('aria-roledescription', 'carousel');
    el.setAttribute('aria-label', el.dataset.carouselLabel || '');
    el.setAttribute('tabindex', '0');
  }

  /**
   * Sync roving tabindex on all bullets in a single pass.
   *
   * Optionally moves focus to the active bullet, used when a slide change
   * is triggered via keyboard while a bullet is focused.
   *
   * @param {HTMLElement} paginationEl - Pagination container element.
   * @param {boolean} moveFocus - Whether to focus the active bullet.
   */
  function syncBulletFocus(paginationEl, moveFocus) {
    let activeBullet = null;
    paginationEl.querySelectorAll(SELECTORS.bullet).forEach(function (bullet) {
      const isActive = bullet.classList.contains(SELECTORS.bulletActive);
      bullet.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) {
        activeBullet = bullet;
      }
    });
    if (moveFocus && activeBullet) {
      activeBullet.focus();
    }
  }

  /**
   * Redirect focus when a nav button becomes disabled after a slide action.
   *
   * Defers via requestAnimationFrame so Swiper has finished updating button
   * state (tabindex, disabled class) before we attempt to move focus.
   *
   * @param {HTMLElement} focusedEl - Element that had focus before the action.
   * @param {HTMLElement|null} prevButton - Previous slide button.
   * @param {HTMLElement|null} nextButton - Next slide button.
   */
  function redirectNavFocus(focusedEl, prevButton, nextButton) {
    if (focusedEl !== prevButton && focusedEl !== nextButton) {
      return;
    }
    requestAnimationFrame(function () {
      if (focusedEl === nextButton && nextButton.classList.contains(SELECTORS.disabled) && prevButton) {
        prevButton.focus();
      }
      else if (focusedEl === prevButton && prevButton.classList.contains(SELECTORS.disabled) && nextButton) {
        nextButton.focus();
      }
    });
  }

  /**
   * Handle keydown events on the swiper container for arrow/home/end navigation.
   *
   * @param {KeyboardEvent} event - The keydown event.
   * @param {object} swiper - Swiper instance.
   * @param {HTMLElement|null} prevButton - Previous slide button.
   * @param {HTMLElement|null} nextButton - Next slide button.
   */
  function handleSwiperKeydown(event, swiper, prevButton, nextButton) {
    // Only handle arrow keys when focus is on the swiper container itself or
    // the pagination dots. Nav buttons use Enter/Space via handleButtonKeydown,
    // and slide content should not trigger slide changes to avoid a mismatch
    // between the focused element and the active slide.
    if (event.target.closest(SELECTORS.slide) ||
        event.target.closest(SELECTORS.prevButton) ||
        event.target.closest(SELECTORS.nextButton)) {
      return;
    }
    const actions = {
      ArrowLeft: () => swiper.slidePrev(),
      ArrowRight: () => swiper.slideNext(),
      Home: () => swiper.slideTo(0),
      End: () => swiper.slideTo(swiper.slides.length - 1)
    };
    const action = actions[event.key];
    if (!action) {
      return;
    }
    event.preventDefault();
    const focusedEl = document.activeElement;
    action();
    redirectNavFocus(focusedEl, prevButton, nextButton);
  }

  /**
   * Render a pagination bullet with role="tab" for NVDA arrow-key navigation.
   *
   * Swiper's default bullets use role="button", which NVDA does not navigate
   * with arrow keys. Using role="tab" inside a role="tablist" container
   * triggers the NVDA native tablist interaction pattern, enabling arrow-key
   * navigation without any additional JavaScript.
   *
   * @param {number} index - Zero-based slide index.
   * @param {string} className - CSS classes provided by Swiper.
   * @return {string} HTML string for the bullet element.
   */
  function renderPaginationBullet(index, className) {
    const label = Drupal.t('Go to slide @num', {'@num': index + 1});
    return '<span class="' + className + '" role="tab" aria-label="' + label + '" tabindex="-1"></span>';
  }

  /**
   * Handle keydown events on nav buttons for Enter/Space activation.
   *
   * @param {KeyboardEvent} event - The keydown event.
   * @param {Function} action - The slide action to invoke.
   * @param {HTMLElement|null} prevButton - Previous slide button.
   * @param {HTMLElement|null} nextButton - Next slide button.
   */
  function handleButtonKeydown(event, action, prevButton, nextButton) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    const focusedEl = document.activeElement;
    action();
    redirectNavFocus(focusedEl, prevButton, nextButton);
  }

  /**
   * Setup keyboard navigation for the gallery.
   *
   * @param {object} swiper - Swiper instance.
   * @param {HTMLElement} swiperEl - The .swiper element.
   * @param {HTMLElement} paginationEl - Pagination container element.
   */
  function setupKeyboardNavigation(swiper, swiperEl, paginationEl) {
    const prevButton = swiperEl.querySelector(SELECTORS.prevButton);
    const nextButton = swiperEl.querySelector(SELECTORS.nextButton);

    swiperEl.addEventListener('keydown', function (event) {
      handleSwiperKeydown(event, swiper, prevButton, nextButton);
    });

    [[prevButton, () => swiper.slidePrev()], [nextButton, () => swiper.slideNext()]]
      .forEach(function ([button, action]) {
        if (!button) {
          return;
        }
        button.addEventListener('keydown', function (event) {
          handleButtonKeydown(event, action, prevButton, nextButton);
        });
      });

    if (!paginationEl) {
      return;
    }

    // Flag set on keydown so paginationUpdate knows whether to move focus.
    let bulletKeyboardNav = false;

    // For full-width containers, paginationUpdate fires during Swiper's init
    // before afterInit runs, so our listener below would miss that first
    // event. This initial call covers that case.
    syncBulletFocus(paginationEl, false);

    // paginationUpdate fires after Swiper's a11y module resets all bullet
    // tabIndex to "0". Our listener is registered in afterInit (after a11y's
    // own handler), so it always runs second and re-applies roving tabindex.
    // For smaller containers, ResizeObserver triggers a second paginationUpdate
    // after init which this handler also catches.
    swiper.on('paginationUpdate', function () {
      const moveFocus = bulletKeyboardNav;
      bulletKeyboardNav = false;
      syncBulletFocus(paginationEl, moveFocus);
    });

    // Stop propagation to avoid double-firing the swiperEl keydown handler
    // when pagination is nested inside .swiper.
    paginationEl.addEventListener('keydown', function (event) {
      const arrowActions = {
        ArrowLeft: () => swiper.slidePrev(),
        ArrowRight: () => swiper.slideNext()
      };
      const action = arrowActions[event.key];
      if (!action) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      bulletKeyboardNav = true;
      action();
    });
  }

  /**
   * Scroll the carousel to keep the focused slide fully visible.
   *
   * Swiper's native scrollOnFocus bails when the focused slide index equals
   * activeIndex, which gets reset to 0 after a ResizeObserver-triggered
   * breakpoint recalculation on smaller containers. This supplement uses
   * getBoundingClientRect to detect actual visibility, ignoring Swiper's
   * internal state. Only acts on keyboard-initiated focus events.
   *
   * @param {object} swiper - Swiper instance.
   * @param {HTMLElement} swiperEl - The .swiper element.
   */
  function setupScrollOnFocus(swiper, swiperEl) {
    let keyboardNav = false;

    swiperEl.addEventListener('pointerdown', function () {
      keyboardNav = false;
    }, true);

    swiperEl.addEventListener('keydown', function (event) {
      // Only Tab navigation should trigger scroll-to-focused-slide.
      // VoiceOver (VO+Arrow) sends real keydown events with arrow keys that
      // would set this flag and cause Swiper to advance through off-screen
      // slides as VoiceOver moves its virtual cursor through the DOM.
      if (event.key === 'Tab') {
        keyboardNav = true;
      }
    }, true);

    swiperEl.addEventListener('focusin', function (event) {
      if (!keyboardNav) {
        return;
      }
      // Reset immediately so subsequent VoiceOver focusin events cannot
      // chain off a Tab-initiated flag that is no longer relevant.
      keyboardNav = false;
      const slide = event.target.closest(SELECTORS.slide);
      if (!slide) {
        return;
      }
      const index = Array.from(swiper.slides).indexOf(slide);
      if (index === -1) {
        return;
      }
      const swiperRect = swiperEl.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      if (slideRect.left < swiperRect.left || slideRect.right > swiperRect.right) {
        swiper.slideTo(index);
      }
    }, true);
  }

  /**
   * Initialize a Swiper instance for a gallery element.
   *
   * @param {HTMLElement} element - The .swiper element.
   */
  function initSwiper(element) {
    const gallery = element.closest(SELECTORS.gallery);
    if (!gallery) {
      return;
    }

    // Cache the initial aria-label as data-carousel-label so updateCarouselAria
    // can restore it after a lock/unlock cycle.
    if (!element.dataset.carouselLabel) {
      element.dataset.carouselLabel = element.getAttribute('aria-label') || '';
    }

    const cardCount = Number(gallery.dataset.cardCount) || 0;
    const spacingMap = {2: 24, 3: 16};
    const spacing = spacingMap[cardCount] || 4;

    const nextEl = gallery.querySelector(SELECTORS.nextButton);
    const prevEl = gallery.querySelector(SELECTORS.prevButton);
    const paginationEl = gallery.querySelector(SELECTORS.pagination);

    new Swiper(element, {
      slidesPerView: 'auto',
      watchOverflow: true,
      breakpointsBase: 'container',
      breakpoints: {
        0: {spaceBetween: 12},
        768: {spaceBetween: spacing}
      },
      navigation: {nextEl, prevEl},
      pagination: {
        el: paginationEl,
        clickable: true,
        renderBullet: renderPaginationBullet
      },
      a11y: {
        prevSlideMessage: Drupal.t('Previous slide'),
        nextSlideMessage: Drupal.t('Next slide'),
        slideLabelMessage: ''
      },
      on: {
        afterInit(swiperInstance) {
          setupScrollOnFocus(swiperInstance, element);
          setupKeyboardNavigation(swiperInstance, element, paginationEl);
          updateLinkAriaLabels(swiperInstance);
          updateCarouselAria(swiperInstance, swiperInstance.isLocked);
        },
        lock(swiperInstance) {
          updateCarouselAria(swiperInstance, true);
        },
        unlock(swiperInstance) {
          updateCarouselAria(swiperInstance, false);
        }
      }
    });
  }

  Drupal.behaviors.cardGalleryBehavior = {
    attach: function (context) {
      once('card-gallery-behavior', SELECTORS.swiper, context).forEach(initSwiper);
    }
  };

})(Drupal, once, window.Swiper);
